import React, { useCallback } from 'react';

import { EDashboardPage, ELockoutSource } from 'constants/enums';
import { PreLoginDashboardEventData } from 'hooks/useBigQueryTracking';

import { ELockoutType, EModalState, LockoutState, RateLimitErrorResponse } from '../LoginModal.types';

interface UseLockoutHandlerProps {
  setLockout: (lockout: LockoutState) => void;
  setModalState: React.Dispatch<React.SetStateAction<EModalState>>;
  trackPreLoginEvent: (eventData: PreLoginDashboardEventData) => Promise<void>;
}

export const useLockoutHandler = ({ setLockout, setModalState, trackPreLoginEvent }: UseLockoutHandlerProps) => {
  const handleRateLimitError = useCallback(
    (error: any, lockoutType: ELockoutType): boolean => {
      if (error?.response?.status === 429) {
        const lockoutData = error.response.data as RateLimitErrorResponse;
        const lockoutTime =
          lockoutType === ELockoutType.LOGIN
            ? lockoutData.lockUntil || lockoutData.nextLoginAvailableAt
            : lockoutData.lockUntil || lockoutData.nextOTPAvailableAt;

        if (lockoutTime) {
          const lockoutEndTime = new Date(lockoutTime);
          const cooldownTimeSeconds = Math.ceil((lockoutEndTime.getTime() - Date.now()) / 1000);
          
          setLockout({
            endTime: lockoutEndTime,
            type: lockoutType
          });
          setModalState(EModalState.LOCKOUT);
          
          trackPreLoginEvent({
            page: EDashboardPage.LOCKOUT,
            source: lockoutType === ELockoutType.LOGIN ? ELockoutSource.PASSWORD : ELockoutSource.OTP,
            cooldownTime: cooldownTimeSeconds.toString()
          });
          
          return true;
        }
      }
      return false;
    },
    [setLockout, setModalState, trackPreLoginEvent]
  );

  const handleLockoutEnd = useCallback((): void => {
    setModalState(EModalState.LOGIN);
    setLockout({
      endTime: null,
      type: null
    });
  }, [setLockout, setModalState]);

  return {
    handleRateLimitError,
    handleLockoutEnd
  };
};

